import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, DollarSign, ShoppingBag, Calendar, Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import apiClient, { getAuthToken } from '@/utils/api';

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];

const RevenuePage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredData, setFilteredData] = useState(null);

  useEffect(() => {
    if (!getAuthToken()) {
      navigate('/login');
      return;
    }
    fetchRevenueStats();
    
    // Set default dates to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, [navigate]);

  const fetchRevenueStats = async () => {
    try {
      const response = await apiClient.get('/revenue/stats');
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  const handleFilterApply = async () => {
    if (!startDate || !endDate) {
      toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin');
      return;
    }

    try {
      const response = await apiClient.get('/revenue/filtered', {
        params: { start_date: startDate, end_date: endDate }
      });
      setFilteredData(response.data);
      toast.success('Filtre uygulandı');
    } catch (error) {
      toast.error('Filtreleme sırasında hata oluştu');
    }
  };

  const handleExport = async (format) => {
    if (!startDate || !endDate) {
      toast.error('Lütfen tarih aralığı seçin');
      return;
    }

    try {
      const response = await apiClient.get(`/revenue/export/${format}`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gelir_raporu_${startDate}_${endDate}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Rapor ${format.toUpperCase()} olarak indirildi`);
    } catch (error) {
      toast.error('İndirme sırasında hata oluştu');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <p className="text-slate-600 dark:text-slate-400">Yükleniyor...</p>
        </div>
      </AdminLayout>
    );
  }

  const displayData = filteredData || stats;
  const revenueData = filteredData ? [
    { name: 'Seçili Dönem', value: filteredData.total_revenue }
  ] : [
    { name: 'Günlük', value: stats.daily_revenue },
    { name: 'Aylık', value: stats.monthly_revenue }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gelir Raporlama
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Detaylı gelir analizleri ve istatistikler</p>
        </div>

        {/* Date Filter and Export Section */}
        <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-6">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-4">Tarih Filtresi ve Dışa Aktarma</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
            />
            <Button
              onClick={handleFilterApply}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Calendar size={16} className="mr-2" />
              Filtrele
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={() => handleExport('excel')}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <FileSpreadsheet size={16} className="mr-1" />
                Excel
              </Button>
              <Button
                onClick={() => handleExport('pdf')}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                <Download size={16} className="mr-1" />
                PDF
              </Button>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 backdrop-blur-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 dark:text-slate-400 text-sm">{filteredData ? 'Toplam Gelir' : 'Günlük Gelir'}</p>
              <DollarSign className="text-emerald-400" size={24} />
            </div>
            <p className="text-3xl font-bold text-emerald-400 font-mono">
              {filteredData ? filteredData.total_revenue.toFixed(2) : stats.daily_revenue.toFixed(2)} ₺
            </p>
            <p className="text-slate-500 text-sm mt-1">
              {filteredData ? `${filteredData.total_orders} sipariş` : `${stats.daily_orders} sipariş`}
            </p>
          </Card>

          {!filteredData && (
            <>
              <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 backdrop-blur-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 dark:text-slate-400 text-sm">Aylık Gelir</p>
                  <TrendingUp className="text-blue-400" size={24} />
                </div>
                <p className="text-3xl font-bold text-blue-400 font-mono">{stats.monthly_revenue.toFixed(2)} ₺</p>
                <p className="text-slate-500 text-sm mt-1">{stats.monthly_orders} sipariş</p>
              </Card>

              <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 backdrop-blur-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 dark:text-slate-400 text-sm">Ortalama Sipariş</p>
                  <ShoppingBag className="text-orange-400" size={24} />
                </div>
                <p className="text-3xl font-bold text-orange-400 font-mono">
                  {stats.monthly_orders > 0 ? (stats.monthly_revenue / stats.monthly_orders).toFixed(2) : '0.00'} ₺
                </p>
                <p className="text-slate-500 text-sm mt-1">Aylık ortalama</p>
              </Card>

              <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 backdrop-blur-sm p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-600 dark:text-slate-400 text-sm">Tamamlanan</p>
                  <Calendar className="text-purple-400" size={24} />
                </div>
                <p className="text-3xl font-bold text-purple-400">{stats.monthly_orders}</p>
                <p className="text-slate-500 text-sm mt-1">Bu ay</p>
              </Card>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 backdrop-blur-sm p-6">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Gelir Karşılaştırması
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  labelStyle={{ color: '#f8fafc' }}
                />
                <Bar dataKey="value" fill="#f97316" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 backdrop-blur-sm p-6">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              En Çok Satan Ürünler
            </h2>
            {displayData.top_items && displayData.top_items.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={displayData.top_items}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="revenue"
                    >
                      {displayData.top_items.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-6 space-y-3">
                  {displayData.top_items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-100 dark:bg-slate-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <div>
                          <p className="text-slate-800 dark:text-slate-200 font-medium">{item.name}</p>
                          <p className="text-slate-600 dark:text-slate-400 text-sm">{item.quantity} adet</p>
                        </div>
                      </div>
                      <p className="text-orange-400 font-mono font-bold">{item.revenue.toFixed(2)} ₺</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-slate-600 dark:text-slate-400 text-center py-12">Henüz veri yok</p>
            )}
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default RevenuePage;